import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { TreeService } from 'src/app/services/Tree.service';
import { DataService } from 'src/app/services/Data.service';
import { DeleteSimulationService, DeleteSimulationFilter } from './delete-simulation.service';
import { HeaderActionsService, HeaderActionType } from 'src/app/services/header-actions.service';
import Swal from 'sweetalert2';

export interface DeleteSimulationForm {
    userId: number | null;
    customer: {
        id: number | null;
        description: string;
    };
    region: {
        id: number | null;
        description: string;
    };
    project: {
        id: number | null;
        description: string;
    };
    simulation: {
        id: number | null;
        description: string;
    };
    path: string;
}

@Component({
    selector: 'app-delete-simulation-modal',
    templateUrl: './delete-simulation-modal.component.html',
    styleUrls: ['./delete-simulation-modal.component.css']
})
export class DeleteSimulationModalComponent implements OnInit {
    @Output() close = new EventEmitter<void>();

    nodes: any[] = [];
    customers: any[] = [];
    regions: any[] = [];
    projects: any[] = [];
    simulations: any[] = [];

    userId: number | undefined;
    module: string | undefined;
    department: string | undefined;
    rol: string | undefined;

    filteredSimulations$: Observable<any[]>;
    selectedSimulation$: Observable<any>;

    constructor(
        private treeService: TreeService,
        private dataService: DataService,
        private deleteSimulationService: DeleteSimulationService,
        private headerActionsService: HeaderActionsService
    ) {
        this.filteredSimulations$ = this.deleteSimulationService.filteredSimulations$;
        this.selectedSimulation$ = this.deleteSimulationService.selectedSimulation$;
    }

    ngOnInit(): void {
        this.userId = this.dataService.getCurrentUserId();
        this.module = this.dataService.getCurrentModule();
        this.loadSimulationsData();
    }

    private loadSimulationsData() {
        if (this.userId && this.module) {
            this.treeService.getTreeByUserId(this.userId, this.module).subscribe(levels => {
                if (levels.status.success) {
                    this.nodes = levels.data;
                    this.mapNodesToFiltersAndSimulations();
                }
            });
        }
    }

    mapNodesToFiltersAndSimulations() {
        this.customers = this.nodes.filter(x => x.item_level === 'customer').map(x => ({ id: x.id, name: x.item_name, parent_id: x.parent_id }));
        this.regions = this.nodes.filter(x => x.item_level === 'region').map(x => ({ id: x.id, name: x.item_name, parent_id: x.parent_id }));
        this.projects = this.nodes.filter(x => x.item_level === 'project').map(x => ({ id: x.id, name: x.item_name, parent_id: x.parent_id }));
        const simulationsData = this.nodes.filter(x => x.item_level === 'simulation').map(x => ({
            user: this.getParentName(x, 'user'),
            customer: this.getParentName(x, 'customer'),
            region: this.getParentName(x, 'region'),
            project: this.getParentName(x, 'project'),
            parent_id: x.parent_id,
            name: x.item_name,
            id: x.id
        }));
        this.simulations = simulationsData;
        this.deleteSimulationService.setSimulations(simulationsData);
    }

    getParentName(node: any, level: string): { id: any; name: string } {
        let current = node;
        while (current && current.parent_id) {
            const parent = this.nodes.find(x => x.id === current.parent_id);
            if (parent && parent.item_level === level) {
                return { id: parent.id, name: parent.item_name };
            }
            current = parent;
        }
        return { id: null, name: '' };
    }

    onFilterChange(filter: DeleteSimulationFilter) {
        this.deleteSimulationService.setFilters(filter);
    }

    onClose(): void {
        this.deleteSimulationService.setSelectedSimulation(null);
        this.close.emit();
    }

    handleDeleteSimulation(simulation?: any): void {
        const sim = simulation || this.deleteSimulationService.getSelectedSimulationValue();

        if (!sim) {
            return;
        }

        this.deleteSimulationService.checkDeletableLevels(sim.id).subscribe((deletableLevels: any) => {
            const data = deletableLevels?.data || {};

            const can = {
                customer: !!data.customer?.canDelete,
                region: !!data.region?.canDelete,
                project: !!data.project?.canDelete,
                simulation: !!data.simulation?.canDelete,
            };

            const ids = {
                customer: data.customer?.treeNodeId ?? null,
                region: data.region?.treeNodeId ?? null,
                project: data.project?.treeNodeId ?? null,
                simulation: data.simulation?.treeNodeId ?? null,
            };

            const names = {
                customer: data.customer?.treeNodeName ?? null,
                region: data.region?.treeNodeName ?? null,
                project: data.project?.treeNodeName ?? null,
                simulation: data.simulation?.treeNodeName ?? null,
            };

            const simForm = this.mapSimulationToDeleteSimulationForm(sim);

            // If only simulation can be deleted
            if (can.simulation && !can.project && !can.region && !can.customer) {
                const simulationPath = this.mapSimulationToDeleteSimulationForm(sim).path;
                Swal.fire({
                    title: 'Delete simulation?',
                    html: `You can only delete the simulation: <b>${simulationPath}</b>.</br></br>Do you want to continue?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Delete',
                    cancelButtonText: 'Cancel',
                }).then(result => {
                    if (result.isConfirmed) {
                        if (ids.simulation != null) {
                            Swal.fire({
                                title: 'Deleting... ',
                                allowOutsideClick: false,
                                didOpen: () => { Swal.showLoading(); }
                            });
                            this.treeService.deleteNodeTree(ids.simulation).subscribe({
                                next: (resp: { status: { success: any; }; }) => {
                                    Swal.close();
                                    if (resp?.status?.success) {
                                        Swal.fire({ icon: 'success', title: 'Simulation deleted', timer: 1500, showConfirmButton: false });
                                        this.headerActionsService.requestAction(HeaderActionType.Delete, simForm);
                                        this.onClose();
                                    } else {
                                        Swal.fire({ icon: 'error', title: 'Delete failed' });
                                    }
                                },
                                error: () => { Swal.close(); Swal.fire({ icon: 'error', title: 'Delete failed' }); }
                            });
                        }
                    }
                });
            } else {
                let html = '<div style="text-align:left">';
                html += `<label style="display:block; margin:4px 0;"><input type="checkbox" id="chk-customer" ${!can.customer ? 'disabled title="Cannot delete customer with multiple regions"' : ''}> ${!can.customer ? `Customer: <strong>${names.customer}</strong>` : `Delete customer <strong>${names.customer}</strong>`}</label>`;
                html += `<label style="display:block; margin:4px 0;"><input type="checkbox" id="chk-region" ${!can.region ? 'disabled title="Cannot delete region with multiple projects"' : ''}> ${!can.region ? `Region: <strong>${names.region}</strong>` : `Delete region <strong>${names.region}</strong>`}</label>`;
                html += `<label style="display:block; margin:4px 0;"><input type="checkbox" id="chk-project" ${!can.project ? 'disabled title="Cannot delete project with multiple simulations"' : ''}> ${!can.project ? `Project: <strong>${names.project}</strong>` : `Delete project <strong>${names.project}</strong>`}</label>`;
                html += `<label style="display:block; margin:4px 0;"><input type="checkbox" id="chk-simulation" checked disabled> Delete simulation <strong>${names.simulation}</strong></label>`;
                html += '</div>';

                Swal.fire({
                    title: 'Select levels to delete',
                    html,
                    focusConfirm: false,
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: 'Delete and close',
                    denyButtonText: 'Delete and keep open',
                    cancelButtonText: 'Cancel',
                    didOpen: () => {
                        // Hierarchy: Customer > Region > Project > Simulation
                        const chkCustomer = document.getElementById('chk-customer') as HTMLInputElement;
                        const chkRegion = document.getElementById('chk-region') as HTMLInputElement;
                        const chkProject = document.getElementById('chk-project') as HTMLInputElement;
                        const chkSimulation = document.getElementById('chk-simulation') as HTMLInputElement;

                        // Helper to set check without triggering infinite events
                        function setChecked(el: HTMLInputElement | null, checked: boolean) {
                            if (el) el.checked = checked;
                        }

                        // When checking customer, check all children. When unchecking, only customer
                        if (chkCustomer) {
                            chkCustomer.addEventListener('change', () => {
                                if (chkCustomer.checked) {
                                    setChecked(chkRegion, true);
                                    setChecked(chkProject, true);
                                    setChecked(chkSimulation, true);
                                } else {
                                    setChecked(chkCustomer, false);
                                }
                            });
                        }
                        
                        // When checking region, check children. When unchecking, region and customer
                        if (chkRegion) {
                            chkRegion.addEventListener('change', () => {
                                if (chkRegion.checked) {
                                    setChecked(chkProject, true);
                                    setChecked(chkSimulation, true);
                                } else {
                                    setChecked(chkRegion, false);
                                    setChecked(chkCustomer, false);
                                }
                            });
                        }
                        
                        // When checking project, check child. When unchecking, project, region and customer (NOT simulation)
                        if (chkProject) {
                            chkProject.addEventListener('change', () => {
                                if (chkProject.checked) {
                                    setChecked(chkSimulation, true);
                                } else {
                                    setChecked(chkProject, false);
                                    setChecked(chkRegion, false);
                                    setChecked(chkCustomer, false);
                                }
                            });
                        }

                        // Simulation is always checked and disabled, no event listener needed
                    },
                    preConfirm: () => {
                        return {
                            simulation: true, // Always true since it is always checked and disabled
                            project: (document.getElementById('chk-project') as HTMLInputElement)?.checked || false,
                            region: (document.getElementById('chk-region') as HTMLInputElement)?.checked || false,
                            customer: (document.getElementById('chk-customer') as HTMLInputElement)?.checked || false,
                        };
                    },
                    preDeny: () => {
                        return {
                            simulation: true, // Always true since it is always checked and disabled
                            project: (document.getElementById('chk-project') as HTMLInputElement)?.checked || false,
                            region: (document.getElementById('chk-region') as HTMLInputElement)?.checked || false,
                            customer: (document.getElementById('chk-customer') as HTMLInputElement)?.checked || false,
                        };
                    }
                }).then(result => {
                    this.executeDeletionByHierarchy(result.value, ids, result.isConfirmed, simForm);
                });
            }
        });
    }

    private executeDeletionByHierarchy(selected: any, ids: { customer: number|null, region: number|null, project: number|null, simulation: number|null }, closeOnSuccess: boolean, simForm: DeleteSimulationForm) {
        // Choose the highest selected hierarchical level
        let targetId: number | null = null;
        if (selected?.customer) targetId = ids.customer;
        else if (selected?.region) targetId = ids.region;
        else if (selected?.project) targetId = ids.project;
        else if (selected?.simulation) targetId = ids.simulation;

        if (targetId == null) {
            Swal.fire({ icon: 'info', title: 'No level selected' });
            return;
        }

        Swal.fire({
            title: 'Deleting... ',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
        });

        this.treeService.deleteNodeTree(targetId).subscribe({
            next: (resp: { status: { success: any; }; }) => {
                Swal.close();
                if (resp?.status?.success) {
                    Swal.fire({ icon: 'success', title: 'Deleted', timer: 1500, showConfirmButton: false });
                    if (closeOnSuccess) {
                        this.onClose();
                    } else {
                        // Keep open: refresh data to reflect changes
                        this.loadSimulationsData();
                    }

                    this.headerActionsService.requestAction(HeaderActionType.Delete, simForm);
                } else {
                    Swal.fire({ icon: 'error', title: 'Delete failed' });
                }
            },
            error: () => { Swal.close(); Swal.fire({ icon: 'error', title: 'Delete failed' }); }
        });
    }

    private mapSimulationToDeleteSimulationForm(sim: any): DeleteSimulationForm {
        return {
            userId: sim.user?.id ?? null,
            customer: {
                id: sim.customer?.id ?? null,
                description: sim.customer?.name ?? ''
            },
            region: {
                id: sim.region?.id ?? null,
                description: sim.region?.name ?? ''
            },
            project: {
                id: sim.project?.id ?? null,
                description: sim.project?.name ?? ''
            },
            simulation: {
                id: sim.id ?? null,
                description: sim.name ?? ''
            },
            path: [
                sim.customer?.name ?? '',
                sim.region?.name ?? '',
                sim.project?.name ?? '',
                sim.name ?? ''
            ].filter(Boolean).join('-')
        };
    }
}